import * as React from 'react';

import Link from 'components/atoms/Link';
import ButtonBar from 'components/molecules/ButtonBar';
import CardLike from 'components/molecules/Card/elements/Like';
import CARD_RENDER_LOCATION from 'containers/molecules/Card/enum-card-render-location';
import Recipe from 'containers/molecules/Recipe/Recipe';
import routes from 'defs/routes';
import Sequences from 'defs/sequences';
import { delayed } from 'lib/Job';

import i18n from 'helper/i18n';
import { compileDynamicRoute } from 'helper/string';

import {
	FILTER_ID as MENU_TYPE_FILTER_ID,
	getFilterByFilterTagId as getMenuTypeFilterByFilterTagId,
} from 'components/molecules/TagFilter/factories/recipe/menuType';
import { ContainerCardDetailRecipePropsType } from 'containers/molecules/Card/Detail/Recipe';
import { ContainerCardDetailRecipeFavoritePropsType } from 'containers/molecules/Card/Detail/RecipeFavorite';
import { HistoryType } from 'types/withRouter';
import { getMenuTypeFilterTagId, getRecipeCalories } from '../../../../store/programItems';
import { ButtonProps } from '../../Button/Button';
import {
	FILTER_ID as CALORIES_FILTER_ID,
	getFilterValueKeyByCalories,
} from '../../TagFilter/factories/recipe/calories';

export type CardDetailRecipeProps = (
	| ContainerCardDetailRecipePropsType
	| ContainerCardDetailRecipeFavoritePropsType
) & {
	externalId?: string;
	programId?: string;
	programItemExternalId: string;
	contentTypeExternalId: string;
	programItemRef?: string;
	isDayInFuture: boolean;
	menuTypeFilterTagId: ReturnType<typeof getMenuTypeFilterTagId>;
	calories: ReturnType<typeof getRecipeCalories>;
	doSwapItem: (contentType: string, externalId: string, lastDayRef: number) => void;
	doSetProgramItemRef: (ref: number, programItemExternalRef?: string) => void;
	doMoveItem: (contentType: string, programItemExternalId: string, strategy: 'postpone', days?: number) => void;
	history: HistoryType;
	onBackFromDetail: () => {};
};

export class CardDetailRecipe extends React.Component<CardDetailRecipeProps> {
	public static displayName = 'CardDetailRecipe';

	public handleButtonClicks = (buttonType: string) => {
		const { isSingleSequenceLoading } = this.props;

		switch (buttonType) {
			case 'completed':
				if (isSingleSequenceLoading[Sequences.CARD_IS_CHECKING]) {
					return;
				}
				this.props.onComplete({
					type: 'recipe',
					onAnimationDone: () => {
						this.props.doComplete(this.props.id as any);
					},
				});
				break;
			case 'pool':
				if (this.props.menuTypeFilterTagId) {
					const menuTypeFilter = getMenuTypeFilterByFilterTagId(this.props.menuTypeFilterTagId);

					if (menuTypeFilter) {
						this.props.doSetPersistentFilter(
							MENU_TYPE_FILTER_ID,
							getMenuTypeFilterByFilterTagId(this.props.menuTypeFilterTagId) as string
						);
					}
				}
				if (this.props.calories) {
					this.props.doSetPersistentFilter(
						CALORIES_FILTER_ID,
						getFilterValueKeyByCalories(this.props.calories)
					);
				}
				const targetPoolURL = compileDynamicRoute(i18n('react.impulsCoach.routes.pool.cluster.href'), {
					selectedPool: i18n('react.impulsCoach.routes.pool.selectedPool.recipe.href'),
					cluster: '',
				});
				this.props.doSetProgramItemRef(this.props.id, this.props.programItemExternalId);
				delayed(() => {
					this.props.history.push(targetPoolURL);
				}, 1000);
				break;
			case 'select':
				const { doSwapItem, programItemRef, id } = this.props;

				if (id && programItemRef) {
					doSwapItem('recipe', `${id}`, this.props.parentProps.currentDayNumber);
					delayed(() => {
						this.props.history.push(
							`${routes.today.indexRoute.href}/${this.props.parentProps.currentDayNumber || ''}`
						);
					}, 1000);
				}
				break;
			case 'postpone-later':
				this.props.doMoveItem('recipe', `${this.props.id}`, 'postpone', 1);
				break;
			default:
				break;
		}
	};

	public renderInfoText = () => {
		const { energy, totalDurationFormatted, nutrition } = this.props;

		return (
			<div className="m-card__info-text">
				<span className="m-card__info-text-item">{energy}</span>
				{totalDurationFormatted && <span className="m-card__info-text-item">{totalDurationFormatted}</span>}
				{nutrition && (
					<span className="m-card__info-text-item">
						{i18n(
							`react.impulsCoach.components.tagFilter.recipe.RECIPE_DIET_FORM.${nutrition.toUpperCase()}`
						)}
					</span>
				)}
			</div>
		);
	};

	get isDisabled(): boolean {
		const { isSequenceLoading } = this.props;
		return Boolean(isSequenceLoading);
	}

	public renderButtonBar = () => {
		const { isCheckable, uiState, isUiCompleting, cardRenderLocation, parentProps } = this.props;
		let cardButtons: any = null;

		if (cardRenderLocation === CARD_RENDER_LOCATION.PROGRAM_FEED) {
			if (uiState === 'detail') {
				cardButtons = {
					buttons: [
						{
							loading: isUiCompleting,
							disabled: isCheckable,
							knob: {
								modifier: 'dashed',
								size: 'l',
								icon: 'completed',
							},
						},
					],
				} as ButtonProps;
			} else if (this.props.state !== 'folded') {
				if (parentProps.isToday && !parentProps.isLastDay && !parentProps.disablePostponeButton) {
					cardButtons = {
						buttonsLayout: 'links',
						buttons: [
							{
								id: 'change-recipe',
								modifier: '',
								link: {
									iconRight: '',
									iconLeft: 'change',
									title: i18n('react.impulsCoach.components.cardDetail.recipe.poolText'),
									buttonBarId: 'pool',
								},
							},
							{
								id: 'postpone-later',
								modifier: '',
								link: {
									iconRight: '',
									iconLeft: 'close',
									title: i18n('react.impulsCoach.components.cardDetail.recipe.postpone'),
									buttonBarId: 'postpone-later',
								},
							},
						],
					} as ButtonProps;
				} else {
					cardButtons = {
						buttons: [
							{
								id: 'change-recipe',
								modifier: '',
								link: {
									iconRight: '',
									iconLeft: 'change',
									title: i18n('react.impulsCoach.components.cardDetail.recipe.poolText'),
									buttonBarId: 'pool',
								},
							},
						],
					} as ButtonProps;
				}
			}
		}

		if (cardButtons === null) {
			return null;
		}

		return (
			<div className="m-card__button-bar">
				<ButtonBar handleButtonClicks={this.handleButtonClicks} disabled={this.isDisabled} {...cardButtons} />
			</div>
		);
	};

	public render() {
		const {
			title,
			id,
			isDone,
			isCheckable,
			handleCardContentClick,
			handleShowDetailsClick,
			handleToggleFavorite,
			favorite,
			parentProps,
			cardRenderLocation,
			programItemRef,
		} = this.props;

		const overrideSelectedContent = !!~[CARD_RENDER_LOCATION.FAVORITE, CARD_RENDER_LOCATION.POOL].indexOf(
			cardRenderLocation
		);

		return (
			<div>
				<div className="m-card__content" onClick={handleCardContentClick}>
					<div className="m-card__info m-card__info--has-like">
						{title && <h4 className="m-card__info-title">{title}</h4>}
						<CardLike liked={favorite} onClick={handleToggleFavorite} />
						<div className="m-card__info-line">{this.renderInfoText()}</div>
					</div>
					<div className="m-card__link js-m-card__link">
						<Link
							onClick={handleShowDetailsClick}
							title={i18n('react.impulsCoach.components.cardDetail.recipe.linkText')}
							modifier="button"
							openInNewWindow={false}
							iconRight={null}
						/>
					</div>
					<div className="m-card__detail">
						<Recipe
							id={id}
							selectedContent={overrideSelectedContent ? parentProps : null}
							isDone={isDone}
							isCheckable={isCheckable}
							isUpdating={this.props.isUiCompleting}
							isDisabled={this.isDisabled}
							shouldShowSwapButton={!!programItemRef}
							handleButtonClicks={this.handleButtonClicks}
							cardRenderLocation={this.props.cardRenderLocation}
							onClickClose={this.props.onBackFromDetail}
							uiState={this.props.uiState}
						/>
					</div>
					{this.renderButtonBar()}
				</div>
			</div>
		);
	}
}

export default CardDetailRecipe;
